import { Card, useCommentCards } from "App/common/useCommentCards";
import { useFocusOnCard } from "App/common/useFocusedCard";
import { useSpaceContext } from "App/common/useSpace";
import { useSpaceCommentsContext } from "App/common/useSpaceComments";
import { getAuthId, useAuth } from "App/components/Auth";
import { useTrack } from "App/components/useEntities/useTrack";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSeekRef } from "../Carousel/useSeekRef";
import { CommentBlockConfig } from "App/common/Comment/blocks";
import { CommentBlock } from "@highnote/server/src/core/entities";
import { v4 as uuidv4 } from "uuid";
import { JOIN_ENTITY_TRIGGER } from "App/components/Auth/util";

export const useNewCard = () => {
  const { focusOnCard } = useFocusOnCard();
  const { orderedCards } = useCommentCards();
  const { track, currentTrackVersion } = useTrack();
  const [isStickerSelectionDialogOpen, setIsStickerSelectionDialogOpen] =
    useState<boolean>(false);
  const { spaceId, space } = useSpaceContext();
  const { joinEntity } = useAuth();
  const { addSpaceComment } = useSpaceCommentsContext();
  const orderedCardsRef = useRef<Card[]>(orderedCards);
  const seekRef = useSeekRef();

  useEffect(() => {
    orderedCardsRef.current = orderedCards;
  }, [orderedCards]);

  const addCard = useCallback(
    async (config: CommentBlockConfig<CommentBlock>, timestamp?: number) => {
      const generateCardAt = (timestamp: number) => {
        const cardId = uuidv4();

        return {
          id: cardId,
          draft: true,
          hidden: false,
          timestamp,
          createdAt: Date.now(),
          comment: {
            id: cardId,
            createdAt: Date.now(),
            timestamp,
            blocks: [config.generateNew({ ...config.blockProps })],
            spaceId,
            trackId: track?.id ?? "",
            trackVersionIds: currentTrackVersion?.id
              ? [currentTrackVersion.id]
              : [],
            createdBy: getAuthId(),
          },
        };
      };

      const newCard = generateCardAt(
        timestamp ?? seekRef.current.getCurrentTime(),
      );

      setIsStickerSelectionDialogOpen(false);

      if (["custom", "sticker"].includes(config.id)) {
        await addSpaceComment({
          id: newCard.id,
          data: newCard.comment,
        });

        joinEntity({
          entity: space,
          entityType: "Space",
          trigger: JOIN_ENTITY_TRIGGER.COMMENT,
        });
        return;
      }

      focusOnCard(newCard.id, newCard, { edit: true });
    },
    [track?.id, currentTrackVersion?.id, space, joinEntity, addSpaceComment],
  );

  return {
    addCard,
    isStickerSelectionDialogOpen,
    setIsStickerSelectionDialogOpen,
  };
};
